/* =============================================================================
   Piklo v4 — cinematic stage controller.

   v2's engine, restaged: capability gate, lazy 3D load, panel crossfades —
   plus the two moves the reference site is built on:
     1. the stage background lerps through the brand palette as you scroll
     2. a giant display word passes behind the can, one per panel

   Everything still degrades to the static can + stacked copy on phones,
   reduced-motion, or missing WebGL. Three.js is never requested on those.
   ========================================================================== */
(function () {
  'use strict';

  const stage = document.querySelector('[data-stage4]');
  if (!stage) return;

  // calm mode: honor prefers-reduced-motion by removing all motion that is
  // NOT driven by the user's own scrolling — idle drift, bob, bubbles
  const calm = matchMedia('(prefers-reduced-motion: reduce)').matches;

  const canvas = stage.querySelector('[data-can-canvas]');
  const fallback = stage.querySelector('[data-can-fallback]');
  const panels = Array.from(stage.querySelectorAll('[data-panel]'));
  const words = Array.from(stage.querySelectorAll('[data-word]'));
  const bubbleHost = stage.querySelector('[data-bubbles]');
  const bar = document.querySelector('[data-v4-bar]');
  const hint = stage.querySelector('[data-hint]');
  const heroExtras = stage.querySelector('[data-hero-extras]');

  /* ---- background keyframes -------------------------------------------
     One colour per panel slice, sampled at the slice midpoints. All from
     the brand guide; the darkest thing here is ink, never a surface.      */
  const DEFAULT_BGS = null;   // beat colours come from BEATS below
  function parseBgs(str) {
    const out = (str || '').split(',').map(hex => {
      let h = hex.trim().replace('#', '');
      if (h.length === 3) h = h.replace(/./g, '$&$&');   // #abc -> #aabbcc
      return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
    }).filter(c => c.every(v => Number.isFinite(v)));
    return out.length >= 2 ? out : null;
  }
  // merchant text field: tolerate 3-digit hex; fall back to the beat colours
  let BGCOLS = null;   // resolved after BEATS is defined


  // Declared up here, not down with the frames renderer: the static path runs
  // BEFORE that point and now reads it too. As a `const` further down it sat in
  // the temporal dead zone, and bootMobileSpin threw the moment it was called.
  const FRAME_COUNT = 48;

  /* ---- capability gate ------------------------------------------------- */
  // Every check records WHY it failed — the verdict is logged to the console
  // and (with ?debug=1) drawn on the page, so "it doesn't work on my machine"
  // is diagnosable from a screenshot instead of a guessing game.
  // hero-cinema.liquid repeats the device checks below in an inline script that
  // adds .is-js before first paint: change one, change the other.
  function gateReport() {
    const r = { pass: true, reasons: [] };
    const fail = (why) => { r.pass = false; r.reasons.push(why); };
    // reduced-motion does NOT force static: the theatre's motion is
    // scroll-coupled (1:1 with user input); we only strip the idle drift,
    // bob and bubbles. See `calm` below.
    // touch-primary devices are phones/tablets regardless of orientation —
    // a landscape iPhone reports innerWidth 844+ and would slip a width test,
    // but every one of them reports a coarse pointer
    if (matchMedia('(hover: none), (pointer: coarse)').matches) fail('touch-primary device');
    // NO width or height checks at all. Phones and tablets are already
    // excluded by their coarse pointer; a narrow window on a desktop (VSCode
    // preview pane, split screen) is still a desktop and gets the theatre.
    // This was learned twice: a height test benched a MacBook, then a width
    // test benched the VSCode preview pane.
    if (navigator.deviceMemory && navigator.deviceMemory < 4) fail('low device memory');
    if (navigator.hardwareConcurrency && navigator.hardwareConcurrency < 4) fail('few CPU cores');
    // WebGL is NOT checked here: without it the stage runs the pre-rendered
    // frame sequence instead — same can, no GPU required.
    return r;
  }

  function hasWebGL() {
    if (/[?&]frames=1/.test(location.search)) return false;   // force frames
    try {
      const c = document.createElement('canvas');
      return !!(c.getContext('webgl2') || c.getContext('webgl'));
    } catch (e) { return false; }
  }

  function capable() {
    // ?force3d=1 overrides everything — demo insurance
    if (/[?&#](force3d|motion)=?1?\b/.test(location.search + location.hash)) {
      console.info('[piklo] 3D forced via URL flag');
      return true;
    }
    const r = gateReport();
    console.info('[piklo] 3D gate:', r.pass ? 'PASS' : 'STATIC — ' + r.reasons.join('; '));
    if (/[?&]debug=1/.test(location.search)) {
      const chip = document.createElement('div');
      chip.style.cssText = 'position:fixed;left:10px;bottom:10px;z-index:99;background:#0B4622;color:#F7F2EB;' +
        'font:600 12px/1.5 ui-monospace,monospace;padding:10px 14px;border-radius:10px;max-width:420px';
      chip.textContent = r.pass ? '3D gate: PASS' : '3D gate: STATIC — ' + r.reasons.join(' · ');
      document.body.appendChild(chip);
    }
    return r.pass;
  }

  /* ---- static path ------------------------------------------------------ */
  function useStatic() {
    stage.classList.remove('is-js');
    stage.classList.add('is-static');
    if (canvas) canvas.remove();
    if (fallback) fallback.hidden = false;
    if (bar) bar.remove();                       // a progress bar would lie here
    bootMobileSpin();
  }

  /* ---- mobile: the can turns as you scroll past it -----------------------
     Phones never pass the 3D gate, so the hero was a still. The theme already
     ships 48 pre-rendered turntable frames for the no-WebGL path, and this
     reuses them WITHOUT the pinned theatre: the can sits in the flow exactly
     where the still sat, and scrubs through one full revolution as it travels
     up the viewport. No layout change, no three.js.

     The whole design here is a memory budget. Holding all 48 frames as decoded
     840x1680 bitmaps is 271MB, which a phone will not survive. So:
       - every SECOND frame, 24 stops, 15 degrees apart — smooth under a thumb
       - decoded straight to 420x840 by createImageBitmap's own resizer, so the
         full-size bitmap is never materialised at all
     That is ~34MB resident and ~1.4MB over the wire, fetched only after the
     page has settled, and skipped outright on Save-Data or a 2g line.         */
  function bootMobileSpin() {
    if (!fallback) return;
    if (!matchMedia('(max-width: 760px)').matches) return;
    if (calm) return;                                  // reduced motion: keep the still
    if (typeof createImageBitmap !== 'function' || typeof fetch !== 'function') return;
    if (!stage.dataset.turn) return;
    var conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (conn && (conn.saveData || /(^|-)2g$/.test(conn.effectiveType || ''))) return;

    /* The pre-sized set is 24 frames, 15 degrees apart, numbered 00..23 — it is
       already every SECOND frame of the 48-frame turntable, so there is no
       stride to apply here. Frame 06 of it is old frame 12: the wordmark square
       to camera, the pose the still is in, so the swap is invisible. */
    var N = 24, HOME = 6;
    var bmps = new Array(N).fill(null);

    var cv = document.createElement('canvas');
    cv.className = fallback.className;               // inherits every size rule
    cv.setAttribute('aria-hidden', 'true');
    /* OUT of the layout until it has a frame, not just invisible: hidden, it
       already held a can-sized slot under the still, a ~340px blank band until
       the first frame arrived, and then the buy panel jumped ~390px up into it
       (layout shift 0.31). (2026-10-19) */
    cv.style.display = 'none';
    fallback.parentNode.insertBefore(cv, fallback.nextSibling);

    /* The frames ship at 480x960 — the size a DPR-3 phone actually needs, and
       the point where 24 resident bitmaps cross ~44MB. Past that a phone starts
       evicting, and an evicted frame is a blank can. Decode at native size. */
    var W = 480, H = 960;
    cv.width = W; cv.height = H;
    var ctx = cv.getContext('2d');

    var drawn = -1, live = false, ticking = false;
    var enterY = 0, turnPx = 1;          // set by measure(), below

    function nearest(k) {
      for (var d = 0; d < N; d++) {
        var a = (k + d) % N, b = (k - d + N) % N;
        if (bmps[a]) return a;
        if (bmps[b]) return b;
      }
      return -1;
    }
    function paint(k) {
      var got = nearest(k);
      if (got < 0 || got === drawn) return;
      ctx.clearRect(0, 0, W, H);
      ctx.drawImage(bmps[got], 0, 0, W, H);
      drawn = got;
      cv.dataset.frame = got;              // which turntable frame is on screen
      if (!live) {
        live = true;
        cv.style.display = '';
        // hide the still only once a real frame is on the canvas, so there is
        // never a gap where neither is painted — the canvas takes over the
        // still's slot in the same layout pass
        fallback.style.setProperty('display', 'none', 'important');
        measure();                                 // now anchor on the canvas itself
      }
    }
    /* The anchor is the can's own position in the DOCUMENT, measured once the
       layout has settled — NOT wherever the page happened to be when the first
       bitmap arrived. Anchoring on a live rect at boot put the wordmark at a
       scroll position that moved as the images above the can finished loading,
       so the can arrived showing its side or its Nutrition Facts panel. */
    function measure() {
      // Before its first frame the canvas is display:none, a zero box at the
      // current scroll position; the still holds the slot it will take.
      var r = (live ? cv : fallback).getBoundingClientRect();
      var h = r.height || 1;
      var top = r.top + scrollY;                 // the can's place in the document
      /* CLAMPED AT ZERO on purpose. The can is already ~150px inside the
         viewport at scroll 0, so anchoring at "first appears" (a negative
         scroll position nobody can reach) started the turn a third of the way
         in and the wordmark was never square-on. Anchoring at the top of the
         page means the pose you land on IS the wordmark. */
      enterY = Math.max(0, top - innerHeight);
      turnPx = Math.max(1, (top + h) - enterY);  // until its bottom clears the top
    }
    function frame() {
      ticking = false;
      var t = (scrollY - enterY) / turnPx;
      t = t < 0 ? 0 : t > 1 ? 1 : t;
      cv.style.setProperty('--p', (t * 2 - 1).toFixed(4));
      // wordmark square-on the moment it appears, one full turn across the pass
      paint(((HOME + Math.round(t * N)) % N + N) % N);
    }
    function onScroll() { if (!ticking) { ticking = true; requestAnimationFrame(frame); } }

    function turnUrl(n) {
      return stage.dataset.turn.replace('{n}', String(n).padStart(2, '0'));
    }
    function grab(n) {
      return fetch(turnUrl(n), { credentials: 'omit' })
        .then(function (r) { return r.ok ? r.blob() : Promise.reject(); })
        .then(function (bl) {
          return createImageBitmap(bl, { resizeWidth: W, resizeHeight: H, resizeQuality: 'high' });
        })
        .then(function (bm) {
          bmps[n] = bm;
          /* A frame that arrives may be a BETTER match than the substitute
             already on the canvas, and `drawn` would otherwise suppress the
             repaint. Invalidate so the next frame() re-picks. */
          drawn = -1;
          measure();
          frame();
        })
        .catch(function () {});
    }

    function load() {
      /* HOME FIRST. The ring below steps by 4 and does not land on the
         wordmark, so the first paint was whatever the ring happened to have —
         the can arrived showing its side. Fetch the wordmark before anything
         else and the swap from still to canvas is invisible. */
      var ring = [HOME];
      for (var k = 0; k < N; k += 4) if (k !== HOME) ring.push(k);
      grab(HOME).then(function () {
        return Promise.all(ring.slice(1).map(grab));
      }).then(function () {
        addEventListener('scroll', onScroll, { passive: true });
        addEventListener('resize', function () { measure(); onScroll(); }, { passive: true });
        measure(); frame();
        var rest = [];
        for (var k = 0; k < N; k++) if (!bmps[k]) rest.push(k);
        // sequentially, so filling in never competes with the page for bandwidth
        rest.reduce(function (chain, k) { return chain.then(function () { return grab(k); }); },
                    Promise.resolve());
      });
    }

    // never in front of first paint or the LCP image
    if (document.readyState === 'complete') setTimeout(load, 400);
    else addEventListener('load', function () { setTimeout(load, 400); });
  }

  // Without JS the CSS base layout is the static page (panels stacked and
  // visible). This class opts INTO the pinned theatre, so no-JS visitors are
  // never left with 560vh of blank cream.
  // On a fine-pointer desktop the inline script at the top of hero-cinema.liquid
  // has normally added it already, before first paint, so a reload's restored
  // scroll position lands on the final layout. `ready` tells that script's
  // failsafe this controller is running; without it the class comes off again.
  stage.classList.add('is-js');
  stage.dataset.ready = '1';


  // "#buy" now lives INSIDE the stage — the closing beat IS the pack picker — so
  // a plain anchor jump would land at the TOP of the tour. Fast-forward the film
  // to its ending instead. (Static path: just bring the picker into view.)
  // Any link to THIS page's #buy counts, not just the literal "#buy": the header,
  // drawer, footer and cart all render "/#buy", and arrivals from other pages carry
  // it in the URL. Matching only the literal made those links do nothing here and
  // land at the top from anywhere else. (2026-10-19)
  const stageBuy = stage.querySelector('[data-buy]');
  const buyPanel = stageBuy ? stageBuy.closest('[data-panel]') : null;
  let snapFilm = null;   // set by the theatre wiring below; stays null on the static path
  function goBuy(smooth) {
    // calm: one step. A smooth scroll here plays the whole film for the visitor.
    const behavior = smooth && !calm ? 'smooth' : 'auto';
    if (stage.classList.contains('is-js')) {
      const total = stage.offsetHeight - innerHeight;
      scrollTo({ top: stage.offsetTop + total * 0.97, behavior });
      if (behavior === 'auto' && snapFilm) snapFilm();
    } else {
      // Centre the picker in the band BELOW the sticky header, by hand. A
      // scrollIntoView centre knew nothing of the header, and it centred above
      // the buy bar's scroll-padding (the bar is off once the picker is on
      // screen), so phones landed with the price under the header. Taller than
      // the band (360px phones, landscape), it top-aligns just under it.
      // Measured at rest: in its first second the beat is still rising 18px into
      // place (heroRise), and a /#buy arrival measured mid-rise landed that high,
      // the ticket under the header at 360px. (2026-10-20)
      const rising = buyPanel && buyPanel.getAnimations ? buyPanel.getAnimations({ subtree: true })
        .filter((a) => a.playState === 'running' && a.effect && a.effect.getComputedTiming().endTime < Infinity) : [];
      Promise.all(rising.map((a) => a.finished.catch(() => {}))).then(() => {
        const hh = (document.getElementById('shopify-section-header') || {}).offsetHeight || 0;
        const r = stageBuy.getBoundingClientRect();
        scrollTo({ top: scrollY + r.top - hh - Math.max(8, (innerHeight - hh - r.height) / 2), behavior });
      });
    }
  }
  if (stageBuy) {
    addEventListener('click', function (e) {
      // modified clicks keep their native new-tab / new-window behaviour
      if (e.defaultPrevented || e.button || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = e.target && e.target.closest ? e.target.closest('a') : null;
      if (!a || a.hash !== '#buy' || a.origin !== location.origin || a.pathname !== location.pathname) return;
      e.preventDefault();
      goBuy(true);
    });
    addEventListener('hashchange', function () { if (location.hash === '#buy') goBuy(true); });
    // Arriving on /#buy: the browser's own jump lands near the TOP of the pinned
    // tour, so take over once the page has settled. A reload or back/forward keeps
    // the position the browser restores instead of yanking the visitor back here.
    const nav = performance.getEntriesByType ? performance.getEntriesByType('navigation')[0] : null;
    const restored = nav && (nav.type === 'reload' || nav.type === 'back_forward');
    if (location.hash === '#buy' && !restored) {
      addEventListener('load', function () { requestAnimationFrame(function () { goBuy(false); }); });
    }
  }

  if (!capable()) { useStatic(); return; }

  /* ---- scroll wiring --------------------------------------------------- */
  let scene = null;
  // frames-renderer state — declared above the loop that reads it
  let framesEl = null, frameImgs = null;
  const n = panels.length || 1;

  function progress() {
    const r = stage.getBoundingClientRect();
    const total = stage.offsetHeight - innerHeight;
    if (total <= 0) return 0;
    return Math.min(1, Math.max(0, -r.top / total));
  }

  // Tab can now reach the picker while its beat is still faded out (see paint):
  // finish the film so the focused control is on screen. Once the beat is live
  // (clickable), the focus came from a click or the visitor is already there.
  if (buyPanel) buyPanel.addEventListener('focusin', function () {
    if (!stage.classList.contains('is-js') || buyPanel.style.pointerEvents === 'auto') return;
    goBuy(true);
  });

  function paint(p) {
    // captions: one per beat, plateau visibility inside its span
    panels.forEach((el, i) => {
      const b = BEATS[i]; if (!b) { el.style.opacity = 0; return; }
      const mid = (b.p0 + b.p1) / 2, half = (b.p1 - b.p0) / 2;
      let d = Math.abs(p - mid);
      if (i === 0) d = Math.max(0, p - mid);
      if (i === BEATS.length - 1) d = Math.max(0, mid - p);
      const o = Math.max(0, 1 - Math.pow(d / (half * 0.92), 4));
      el.style.opacity = o.toFixed(3);
      el.style.transform = 'translateY(' + ((p - mid) * -40).toFixed(1) + 'px)';
      el.style.pointerEvents = o > 0.55 ? 'auto' : 'none';
      // Never hide the buy beat: visibility:hidden took the whole picker out of
      // the Tab order and the accessibility tree. Opacity and pointer-events
      // still keep it unseen and unclickable until the film reaches it.
      el.style.visibility = (o > 0.05 || el === buyPanel) ? 'visible' : 'hidden';
    });

    // ghost words ride their beat's span too (most beats have none)
    words.forEach((el) => {
      const bi = parseInt(el.dataset.beat || '0', 10);
      const b = BEATS[bi]; if (!b) return;
      const mid = (b.p0 + b.p1) / 2, half = (b.p1 - b.p0) / 2;
      let d = Math.abs(p - mid);
      if (bi === 0) d = Math.max(0, p - mid);
      if (bi === BEATS.length - 1) d = Math.max(0, mid - p);
      const o = Math.max(0, 1 - Math.pow(d / (half * 1.2), 2));
      el.style.opacity = o.toFixed(3);
      el.style.transform = 'translate(-50%,-50%) translateY(' + ((p - mid) * -120).toFixed(1) + 'px)';
    });

    // hero extras (ribbon, satellites, badge) belong to beat 0 and leave
    // with it — they must never haunt the label tour
    if (heroExtras) {
      const b0 = BEATS[0];
      const mid0 = (b0.p0 + b0.p1) / 2, half0 = (b0.p1 - b0.p0) / 2;
      const d0 = Math.max(0, p - mid0);
      const o0 = Math.max(0, 1 - Math.pow(d0 / (half0 * 0.92), 4));
      heroExtras.style.opacity = o0.toFixed(3);
      heroExtras.style.visibility = o0 > 0.04 ? 'visible' : 'hidden';
    }

    // background: interpolate between beat-mid colours — rendered with alpha
    // so the floating world (piklo-world.js) ghosts through behind the can
    const mids = BEATS.map(b => (b.p0 + b.p1) / 2);
    let bi = 0;
    while (bi < mids.length - 1 && p > mids[bi + 1]) bi++;
    const f = Math.min(1, Math.max(0, (p - mids[bi]) / ((mids[bi + 1] || 1) - mids[bi] || 1)));
    const ca = BGCOLS[bi], cb = BGCOLS[Math.min(bi + 1, BGCOLS.length - 1)];
    const mix = ca.map((v, k) => Math.round(v + (cb[k] - v) * (bi < mids.length - 1 ? f : 0)));
    stage.style.background = 'rgba(' + mix.join(',') + ',0.72)';

    if (bar) bar.style.transform = 'scaleX(' + p.toFixed(4) + ')';
    if (hint) {
      /* CSS owns the hint until the page moves. A running animation outranks an
         inline style, so the entrance has to be dropped before the scroll fade
         can take effect — otherwise the hint rides along all the way down. */
      if (p < 0.004) {
        hint.style.opacity = '';
      } else {
        if (!hintReleased) { hint.style.animation = 'none'; hintReleased = true; }
        hint.style.opacity = Math.max(0, 1 - p * 22).toFixed(3);
      }
    }
  }

  // ONE loop owns time: scroll only sets the target, the loop lerps toward it
  // and drives captions, background, the WebGL pose and the frame scrubber
  // from the same smoothed value — nothing can drift out of sync.
  // (Runs only on the theatre path: the gate above already returned for
  // static visitors.)
  let hintReleased = false;
  let target = 0, current = -1, masterRaf = 0;
  /* When the 3D can appears it crossfades with the STILL, which is motionless.
     If the float is already at some point in its cycle the two cans sit up to a
     quarter of a unit apart and the swap reads as a jolt. The float eases in
     from zero over FLOAT_RAMP so the 3D can starts exactly where the still is. */
  let liveAt = 0;

  /* ---- the can flies in ---------------------------------------------------
     The hand-off from the still to the 3D can used to happen IN PLACE. However
     cleanly it was cut, the two cans are different renders at slightly
     different sizes and angles, so the swap always read as a ghost — one can
     blinking into another.

     So the swap no longer happens where you can see it. The 3D can is posed
     well above the top of the frame when it goes live, and floats down into
     the spot the still was occupying. The exchange happens off-screen; what
     you see is a can arriving.

     ENTRY_DROP is in the same world units as the BEATS table (beat y values
     run -3.45 to +0.42), so 7.2 is comfortably out of frame at scale 1. */
  const ENTRY_MS = 950;
  const ENTRY_DROP = 6.0;
  let entryAt = 0;
  const FLOAT_RAMP = 1400;
  function onScroll() { target = progress(); }
  addEventListener('scroll', onScroll, { passive: true });
  addEventListener('resize', onScroll);
  onScroll();
  // An instant jump to #buy (reduced motion, or arriving on /#buy) lands on the
  // closing frame. Left to the lerp, the can would whip through the whole tour in
  // half a second: for a reduced-motion visitor, more motion than the smooth
  // scroll the jump replaced.
  snapFilm = function () { target = current = progress(); };

  // Pointer lean. Fine pointers only (a touch device has no hover state to
  // answer), off in calm mode, and — like the float — scaled to zero as the
  // camera closes in, so it can never make label text swim.
  let ptx = 0, pty = 0, ptxT = 0, ptyT = 0;
  if (!calm && matchMedia('(hover: hover) and (pointer: fine)').matches) {
    addEventListener('pointermove', (e) => {
      ptxT = (e.clientX / innerWidth) * 2 - 1;
      ptyT = (e.clientY / innerHeight) * 2 - 1;
    }, { passive: true });
  }

  let lastT = 0;
  function masterLoop(now) {
    // The static fallback can take over AFTER this loop has started (the 3D
    // module failed to load, see boot). Stop for good: the IntersectionObserver
    // below would otherwise restart it on the next pass through the stage.
    if (stage.classList.contains('is-static')) { masterRaf = 0; return; }
    masterRaf = requestAnimationFrame(masterLoop);
    const t0 = now || performance.now();
    // dt-normalised so the feel is identical on 60Hz and 120Hz displays — a
    // fixed per-frame lerp is literally twice as fast on a ProMotion screen.
    const dt = lastT ? Math.min(0.05, (t0 - lastT) / 1000) : 1 / 60;
    lastT = t0;
    if (current < 0) {
      // first frame: snap. Lerping up from the -1 sentinel took ~1.4s, during
      // which the hero caption slid 40px and the bar crawled — read as a twitch.
      current = target;
    } else {
      // TAU is the time constant: how long the can takes to catch the wheel.
      // 65ms tracks closely enough to feel connected to the mouse and still
      // smooths the wheel's discrete steps into motion. (The old fixed 0.11 per
      // frame worked out to ~143ms on a 60Hz screen and ~72ms on a 120Hz one —
      // the same site felt different on two machines.)
      const k = 1 - Math.exp(-dt / 0.065);
      current += (target - current) * k;
      if (Math.abs(target - current) < 0.0004) current = target;
    }
    paint(current);
    const q = poseAt(current);
    if (!calm) {
      // Float. Amplitude is tied to how close the camera is: at full-can scale
      // the can properly levitates, and by the time the label fills the frame
      // it is zero — a zoomed Nutrition panel that bobs is unreadable.
      const t = performance.now() * 0.001;
      let near = Math.max(0, Math.min(1, (1.35 - q.s) / 0.45));
      if (liveAt) {
        const age = (performance.now() - liveAt) / FLOAT_RAMP;
        if (age < 1) near *= age * age * (3 - 2 * age);   // smoothstep in
      }
      q.y  += Math.sin(t * 0.62) * 0.20 * near + Math.sin(t * 1.13) * 0.04 * near;
      q.rz += Math.sin(t * 0.44) * 0.030 * near + Math.sin(t * 0.9) * 0.012;
      q.x  += Math.sin(t * 0.37) * 0.05 * near;
      // and it leans toward the cursor
      const pk = 1 - Math.exp(-dt / 0.18);
      ptx += (ptxT - ptx) * pk;
      pty += (ptyT - pty) * pk;
      q.x  += ptx * 0.14 * near;
      q.rz += ptx * 0.055 * near;
      q.rx = 0.05 - pty * 0.065 * near;
    }
    /* Applied LAST so it rides on top of the pose, the float and the pointer
       lean rather than being smoothed away by them. A touch of overshoot at
       the end so the can settles rather than stopping dead. */
    if (entryAt) {
      const e = (t0 - entryAt) / ENTRY_MS;
      if (e >= 1) {
        entryAt = 0;
      } else {
        const u = 1 - e;
        /* Plain ease-out cubic: the offset IS u^3, so it starts at exactly
           ENTRY_DROP and reaches 0. An earlier "settle" curve multiplied the
           start by 1.9, which parked the can ~14 units up and left half a
           second of empty hero before it came into frame. The float bob
           already ramps in over FLOAT_RAMP and gives the settle. */
        q.y += ENTRY_DROP * u * u * u;
      }
    }

    if (scene) scene.setPose(q);
    if (framesCtx) drawFramePose(q);
  }
  // NOT started here: the first paint() touches BEATS, which is declared
  // below — kicked off at the end of the script instead .

  /* ---- frames renderer: the no-WebGL theatre ----------------------------
     48 pre-rendered turntable frames of the same can, scrubbed by the same
     scroll keyframes the WebGL scene uses. Frame 12 is the wordmark facing
     camera at rotation 0, matching the 3D rest pose.                        */
  /* =========================================================================
     BEATS — the label tour. One table drives the WebGL can, the frame
     scrubber, the captions, and the background morph.

     Camera grammar: y positions the labelled region at screen centre
     (pos.y = -regionWorldY * s, region heights measured off the production
     wrap), ry brings its face to camera (frame 12 = wordmark front,
     +2.36 = What's-Inside face, +3.93 = Nutrition face), s is the zoom.
     Icon beats are short on purpose; Nutrition dwells. The exit does a full
     extra turn (+2pi) so the can spins home to the wordmark.
     ====================================================================== */
  const BEATS = [
    // ry values are empirical (pose-sweep: nutrition = 0 mod 2pi, wordmark ~1.7,
    // What's-Inside face ~4.25); y values grid-probed against the centreline.
    // x slides the can across the stage and rz leans it into each stop — the
    // can is never parked upright in the middle.
    { p0: 0.00, p1: 0.15, ry: 1.70, x:  0.40, y:  0.00, s: 1.00, rz: -0.10, bg: '#F2F5DC' }, // full can, brine light
    { p0: 0.15, p1: 0.34, ry: 1.95, x: -0.50, y: -3.45, s: 2.50, rz:  0.10, bg: '#E4EDB4' }, // the band, deeper brine
    { p0: 0.34, p1: 0.56, ry: 4.25, x:  0.45, y: -2.40, s: 2.30, rz: -0.08, bg: '#FBFAF0' }, // What's Inside, paper light
    { p0: 0.56, p1: 0.79, ry: 6.28, x:  0.22, y: -1.55, s: 1.95, rz:  0.00, hold: 0.42, bg: '#F8F6EA' }, // Nutrition: dwells square-on for ~1 wheel notch, was ~80vh
    { p0: 0.79, p1: 1.00, ry: 8.00, x:  0.00, y:  0.42, s: 0.88, rz: -0.10, bg: '#C9DE7E' }, // spin home; sits low enough that the float never pushes the lid under the header
  ];

  // Each beat contributes two keys. BETWEEN them the pose is identical, so that
  // stretch is dead scroll — the wheel turns and the can does not move. It used
  // to be 60% of every beat (76% of the nutrition beat, ~80vh of nothing), which
  // is what "I scroll two or three times before anything happens" was.
  // hold is the fraction of the span spent getting IN and OUT, so a BIGGER hold
  // means a SHORTER dead plateau. At 0.44 the plateau is 12% of the beat; the
  // nutrition beat keeps a real dwell so the panel can be read square-on.
  const KEYS = [];
  BEATS.forEach((b, i) => {
    const w = b.p1 - b.p0;
    const h = b.hold == null ? 0.44 : b.hold;
    const last = i === BEATS.length - 1;
    // Beat 0 starts AT p0 (poseAt clamps below the first key — a key at 0.045
    // meant the first 146px of scroll moved nothing at all) and releases almost
    // immediately, so the very first wheel notch moves the can.
    // The exit beat locks late and briefly: it has to arrive home — wordmark to
    // camera behind the CTA — but a full-width plateau parked the can for the
    // last 380px of the theatre.
    if (last) {
      KEYS.push({ p: b.p1 - w * 0.20, ry: b.ry, x: b.x, y: b.y, s: b.s, rz: b.rz });
      KEYS.push({ p: b.p1,            ry: b.ry, x: b.x, y: b.y, s: b.s, rz: b.rz });
      return;
    }
    const kIn  = i === 0 ? b.p0 : b.p0 + w * h;
    const kOut = i === 0 ? b.p0 + w * 0.12 : b.p1 - w * h;
    KEYS.push({ p: kIn,  ry: b.ry, x: b.x, y: b.y, s: b.s, rz: b.rz });
    KEYS.push({ p: kOut, ry: b.ry, x: b.x, y: b.y, s: b.s, rz: b.rz });
  });
  BGCOLS = parseBgs(stage.dataset.bgs) || parseBgs(BEATS.map(b => b.bg).join(','));
  if (BGCOLS.length !== BEATS.length) BGCOLS = parseBgs(BEATS.map(b => b.bg).join(','));

  const easeIO = t => t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
  // ?debug=1 exposes the pose curve so dead scroll can be measured rather than
  // eyeballed — see tools notes in HANDOFF.md
  if (/[?&]debug=1/.test(location.search)) window.__piklo = { poseAt: (p) => poseAt(p), KEYS, BEATS };
  function poseAt(pp) {
    // COPIES at the clamps: the caller mutates the returned pose (sway), and
    // handing back the key object itself let the sway accumulate onto the
    // keyframe as a random walk. The can drifted off-stage. 
    if (pp <= KEYS[0].p) return Object.assign({}, KEYS[0]);
    if (pp >= KEYS[KEYS.length - 1].p) return Object.assign({}, KEYS[KEYS.length - 1]);
    let a = KEYS[0], b = KEYS[KEYS.length - 1];
    for (let i = 0; i < KEYS.length - 1; i++) {
      if (pp >= KEYS[i].p && pp <= KEYS[i + 1].p) { a = KEYS[i]; b = KEYS[i + 1]; break; }
    }
    const t = easeIO(Math.min(1, Math.max(0, (pp - a.p) / ((b.p - a.p) || 1))));
    const l = k => a[k] + (b[k] - a[k]) * t;
    return { ry: l('ry'), y: l('y'), s: l('s'), x: l('x'), rz: l('rz') };
  }

  const FRAME_ANCHOR = 0;               // pose ry maps straight to frame index

  function frameUrl(i) {
    return stage.dataset.frames.replace('{n}', String(i).padStart(2, '0'));
  }

  let framesCtx = null, framesDrawn = null;

  function bootFrames() {
    frameImgs = new Array(FRAME_COUNT).fill(null);
    // A 2D canvas, NOT an <img>: swapping img.src blanks the element while the
    // next frame decodes — that is a white flash on every scroll step. Canvas
    // pixels persist until the next drawImage, so flashing is impossible.
    framesEl = document.createElement('div');
    framesEl.className = 'stage4__frames';
    framesEl.setAttribute('aria-hidden', 'true');
    const cv = document.createElement('canvas');
    cv.width = 620; cv.height = 1240;   // matches the decode size below — see DW/DH
    const sh = document.createElement('div');
    sh.className = 'fshadow';
    framesEl.appendChild(sh);
    framesEl.appendChild(cv);
    framesCtx = cv.getContext('2d');
    stage.querySelector('.stage4__pin').insertBefore(framesEl, stage.querySelector('.stage4__panels'));

    let firstShown = false;
    const ready = (i, im) => {
      frameImgs[i] = im;
      if (!firstShown) {
        firstShown = true;
        stage.classList.add('is-live', 'is-frames');
        entryAt = performance.now();
        if (fallback) fallback.hidden = true;
      }
    };
    /* BOUND THE MEMORY. Holding 48 frames as decoded 840x1680 bitmaps is
       ~271MB, which is enough to get the tab killed on exactly the weaker
       machines this fallback exists to serve. createImageBitmap decodes
       straight to the size we draw at, so the full-size bitmap is never
       materialised: 48 frames at 620x1240 is ~147MB, and at the 640px the
       canvas is actually painted into that is all the detail there is.
       Raised by a peer review, 2026-08-15. */
    const DW = 620, DH = 1240;
    const canResize = typeof createImageBitmap === 'function' && typeof fetch === 'function';

    const load = (i) => {
      if (canResize) {
        fetch(frameUrl(i), { credentials: 'omit' })
          .then((r) => (r.ok ? r.blob() : Promise.reject()))
          .then((bl) => createImageBitmap(bl, { resizeWidth: DW, resizeHeight: DH, resizeQuality: 'high' }))
          .then((bm) => ready(i, bm))
          .catch(() => {});
        return;
      }
      const im = new Image();
      im.onload = () => {
        // pre-decode off the hot path so drawImage never stalls mid-scroll
        if (im.decode) im.decode().then(() => ready(i, im), () => ready(i, im));
        else ready(i, im);
      };
      im.src = frameUrl(i);
    };
    // coarse ring first so scrubbing works immediately, then fill in
    for (let i = 0; i < FRAME_COUNT; i += 6) load(i);
    setTimeout(() => { for (let i = 0; i < FRAME_COUNT; i++) if (!frameImgs[i]) load(i); }, 600);
  }

  function nearestFrame(i) {
    for (let d = 0; d < FRAME_COUNT; d++) {
      const a = (i + d) % FRAME_COUNT, b = (i - d + FRAME_COUNT) % FRAME_COUNT;
      if (frameImgs[a]) return frameImgs[a];
      if (frameImgs[b]) return frameImgs[b];
    }
    return null;
  }

  function drawFramePose(q) {
    const idx = ((Math.round(FRAME_ANCHOR + (q.ry / (Math.PI * 2)) * FRAME_COUNT) % FRAME_COUNT) + FRAME_COUNT) % FRAME_COUNT;
    const im = nearestFrame(idx);
    if (im && framesDrawn !== im) {
      framesCtx.clearRect(0, 0, 620, 1240);
      framesCtx.drawImage(im, 0, 0, 620, 1240);
      framesDrawn = im;
    }
    const unit = innerHeight * 0.163;
    framesEl.style.transform =
      'translate(-50%, -50%) translate(' + (q.x * unit).toFixed(1) + 'px, ' + (-q.y * unit).toFixed(1) + 'px)' +
      ' scale(' + q.s.toFixed(3) + ') rotate(' + (-(q.rz || 0) * 57.2958).toFixed(2) + 'deg)';
  }

  /* ---- bubbles (only on the live path, never in calm mode) --------------- */
  if (bubbleHost && !calm) {
    for (let i = 0; i < 12; i++) {
      const b = document.createElement('span');
      const size = 5 + Math.round(Math.random() * 14);
      b.className = 'b4';
      b.style.width = b.style.height = size + 'px';
      b.style.left = (4 + Math.random() * 92).toFixed(1) + '%';
      b.style.bottom = '-4vh';
      b.style.animationDuration = (9 + Math.random() * 9).toFixed(1) + 's';
      b.style.animationDelay = (Math.random() * 10).toFixed(1) + 's';
      bubbleHost.appendChild(b);
    }
  }

  /* ---- 3D path: load after first paint ----------------------------------- */
  function loadImage(url) {
    // img.decode() before handing it over: the decode of a 3000px wrap otherwise
    // happens synchronously inside init(), stalling the very frame the can
    // appears on. NOT createImageBitmap — three.js uploads an ImageBitmap
    // without applying flipY, and the whole wrap comes out upside down.
    return new Promise((resolve) => {
      if (!url) return resolve(null);
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => { (img.decode ? img.decode().catch(() => {}) : Promise.resolve()).then(() => resolve(img)); };
      img.onerror = () => resolve(null);
      img.src = url;
    });
  }

  // the still's settle animation runs 0.08s + 1.15s; the 3D can must not cross
  // fade in while that is still moving
  const ENTRANCE_MS = 1280;
  const bootT0 = performance.now();

  function boot() {
    // No WebGL (or ?frames=1): the pre-rendered spin. Same can, zero GPU.
    if (!hasWebGL()) {
      if (canvas) canvas.remove();
      console.info('[piklo] renderer: frames (no WebGL)');
      bootFrames();
      return;
    }
    console.info('[piklo] renderer: webgl');
    // resolve against the document — a bare specifier resolves against the
    // script URL and lands in /assets/assets/ (kept fixed since v2)
    const moduleUrl = new URL(canvas.dataset.module, document.baseURI).href;
    // IN PARALLEL. These used to be chained — texture, THEN module — so 693KB
    // of wrap and 671KB of three.js took turns, and the static can sat on
    // screen for the sum of the two. Nothing in the import needs the texture.
    Promise.all([loadImage(canvas.dataset.label), import(moduleUrl)])
      .then(([label, mod]) => {
        /* The DOWNLOAD starts immediately (above) but the BUILD waits for the
           entrance. Evaluating three.js, generating the PMREM env map, uploading
           a 3000px texture and compiling shaders is one long main-thread block —
           measured at over 2s on a software renderer — and running it under the
           opening animation is what made the first second stutter. The still is
           on screen and settled the whole time, so nothing is missing. */
        const wait = Math.max(0, ENTRANCE_MS - (performance.now() - bootT0));
        setTimeout(function () {
          scene = mod.init({
            canvas,
            label,
            tone: canvas.dataset.tone || 'studio',
            // No ground shadow. buildCan's mesh sits behind opts.shadow if it is ever wanted.
            shadow: false,
            labelU: canvas.dataset.labelU ? parseFloat(canvas.dataset.labelU) : 0.75,
            calm: calm,
          });
          // Pose it BEFORE it is visible. init() renders one frame from the
          // module's own idle pose table; showing that and then snapping to the
          // beat pose is a second twitch.
          /* Order matters: size the buffer, THEN pose and draw into it, THEN
             reveal. Revealing first shows one frame of the module's own idle
             pose and the can visibly jumps.

             And warm it before it is seen: the FIRST draw of a new scene pays
             for shader compilation and the texture upload, which is a long
             frame. Two draws while the canvas is still transparent, a frame
             apart, means the reveal starts on an already-warm pipeline. */
          if (scene.resize) scene.resize();
          const first = poseAt(current < 0 ? target : current);
          scene.setPose(first, true);                  // compile + upload here
          requestAnimationFrame(function () {
            if (!scene) return;
            scene.setPose(poseAt(current < 0 ? target : current), true);
            liveAt = performance.now();
            entryAt = liveAt;
            stage.classList.add('is-live');
          });
        }, wait);
      })
      .catch((err) => {
        console.warn('[piklo] 3D unavailable, using static hero:', err);
        /* The theatre was already running. Stop it and hand the panels back to
           the stylesheet: paint()'s inline opacity, visibility and
           pointer-events were worked out for the PINNED layout, and left on the
           stacked panels they hid most of them and faded the buy panel past
           clicking. (masterRaf is safe to touch here; inside useStatic() it is
           not, the gate calls that before the `let` above has run.) */
        if (masterRaf) { cancelAnimationFrame(masterRaf); masterRaf = 0; }
        removeEventListener('scroll', onScroll);
        removeEventListener('resize', onScroll);
        panels.concat(words).forEach(function (el) {
          el.style.opacity = el.style.transform = el.style.visibility = el.style.pointerEvents = '';
        });
        if (heroExtras) heroExtras.style.opacity = heroExtras.style.visibility = '';
        if (hint) hint.style.opacity = hint.style.animation = '';
        stage.style.background = '';
        useStatic();
      });
  }

  masterLoop();

  // Boot the renderer NOW. This used to wait for `load` + requestIdleCallback,
  // which put the static can on screen for ~2.2s and then swapped it for the
  // 3D one — that swap IS the twitch. The hero is the most important asset on
  // the page; it does not queue behind anything.
  boot();

  /* Stop rendering while the stage is nowhere near the viewport.
     The loop used to run for the life of the page, so on an eleven-screen
     homepage the WebGL scene kept drawing a full-size frame the whole time
     somebody was reading the FAQ. Browsers already throttle rAF in a hidden
     TAB; they do not throttle a visible page whose canvas has scrolled away.
     Raised by a peer review, 2026-08-15.

     The margin is deliberately generous — the loop must already be warm before
     the stage comes back into view, or the first frame after resuming shows a
     stale pose. On resume `current` is snapped to `target` for the same reason. */
  if ('IntersectionObserver' in window) {
    let paused = false;
    new IntersectionObserver(function (entries) {
      const near = entries[0].isIntersecting;
      if (!near && !paused) {
        paused = true;
        if (masterRaf) { cancelAnimationFrame(masterRaf); masterRaf = 0; }
      } else if (near && paused) {
        paused = false;
        current = target;              // no lerp from a stale position
        lastT = 0;                     // and no giant dt on the first frame
        if (!masterRaf) masterLoop();
      }
    }, { rootMargin: '400px 0px 400px 0px' }).observe(stage);
  }

  // Shopify theme editor re-renders sections in place; without teardown each
  // re-render stacks another WebGL context and RAF loop
  document.addEventListener('shopify:section:unload', (e) => {
    if (e.target && e.target.contains && e.target.contains(stage)) {
      if (scene) { scene.destroy(); scene = null; }
      if (masterRaf) cancelAnimationFrame(masterRaf);
      removeEventListener('scroll', onScroll);
      removeEventListener('resize', onScroll);
    }
  });
})();
